
import React, { useState, useEffect } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Icon } from '@/components/ui/icon'
import { Combobox, type ComboboxOption } from '@/components/ui/combobox'
import type { Canvas } from '@/hooks/useCanvas'
import type { Project } from '@/hooks/useProjects'

interface CanvasMoveModalProps {
  isOpen: boolean
  onClose: () => void
  canvas: Canvas | null
  projects: Project[]
  onMove: (canvasId: string, projectId: string | null) => Promise<void>
}

export const CanvasMoveModal: React.FC<CanvasMoveModalProps> = ({
  isOpen,
  onClose,
  canvas,
  projects,
  onMove,
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  // Reset selection when modal opens for a new canvas
  useEffect(() => {
    if (isOpen) {
      setSelectedProjectId('')
    }
  }, [isOpen])

  // Filter out current project and prepare options
  const availableProjects = projects.filter(p => p.uuid !== canvas?.project?.uuid)

  const comboboxOptions: ComboboxOption[] = [
    {
      value: '',
      label: 'Drafts (no project)'
    },
    ...availableProjects.map(project => ({
      value: project.uuid,
      label: project.name
    }))
  ]

  const handleSubmit = async () => {
    if (!canvas) return

    setIsLoading(true)
    try {
      await onMove(canvas.uuid, selectedProjectId || null)
    } catch (error) {
      console.error('Failed to move canvas:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Move Session</AlertDialogTitle>
          <AlertDialogDescription>
            Choose a destination for &quot;{canvas?.title}&quot;
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4">
          <Combobox
            options={comboboxOptions}
            value={selectedProjectId}
            onValueChange={setSelectedProjectId}
            placeholder="Select destination..."
            emptyText="No projects found"
            disabled={isLoading}
            disableSearch={true}
          />

          {comboboxOptions.length === 1 && (
            <p className="text-sm text-muted-foreground mt-2">
              Only drafts available - no other projects to move to.
            </p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Icon name="loader-2" className="mr-2 h-4 w-4 animate-spin" />
                Moving...
              </>
            ) : (
              'Move'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}